import { useEffect, useState } from 'react';
import { Search, Pencil } from 'lucide-react';
import moment from 'moment';
import { API } from '../../Common/Api';
import CommonTable from '../../Common/CommonTable';
import AddAgent from '../../Commponent/AddAgent';
import { ErrorToast } from '../../Utils/toast';
import TextInput from '../../Commponent/TextInput';
import CustomSelectInput from '../../Commponent/SelectInput';
import { Button } from '../../Common/Button';
import DealerAgentsModal from '../../Commponent/DealerAgentsModal';
import EditUserModal from '../../Commponent/EditUserModal';

const Dealer = () => {
  const [dealers, setDealers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedDealer, setSelectedDealer] = useState('');
  const [dealerDataNames, setDealerDataNames] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('thisYear');
  const [summaryData, setSummaryData] = useState(null);
  const [agentsModalOpen, setAgentsModalOpen] = useState(false);
  const [selectedDealerForAgents, setSelectedDealerForAgents] = useState(null);
  // Edit dealer modal
  const [editDealer, setEditDealer] = useState(null);

  const getDealers = async () => {
    setLoading(true);
    try {
      const res = await API.GetDealers({
        page: currentPage,
        limit: itemsPerPage,
        search: selectedDealer || search
      });
      if (res?.status === 200 && res?.success) {
        setDealers(res?.data?.data || []);
        setTotal(res?.data?.pagination?.total || 0);
        setTotalPages(res?.data?.pagination?.totalPages || 1);
      } else {
        setDealers([]);
        setTotal(0);
        setTotalPages(1);
        if (res?.message) ErrorToast(res.message);
      }
    } catch (err) {
      ErrorToast('Failed to fetch dealers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getSummaryData = async (timeFilter = selectedTimeFilter) => {
    const params = {
      thisYear: timeFilter === 'thisYear',
      thisMonth: timeFilter === 'thisMonth',
      LastMonth: timeFilter === 'LastMonth',
    };
    try {
      const res = await API.GetDistributorDealerSummary(params);
      if (res?.status === 200 && res?.success) {
        setSummaryData(res?.data || res);
      }
    } catch (err) {
      console.error('Failed to fetch dealer summary:', err);
    }
  };

  useEffect(() => {
    getDealers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, search, selectedDealer, refresh]);

  useEffect(() => {
    getSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeFilter, refresh]);

  useEffect(() => {
    if (dealers && dealers.length > 0) {
      const uniqueDealers = Array.from(
        new Map(
          dealers.map((d) => [d.username, d])
        ).values()
      );
      setDealerDataNames(uniqueDealers);
    }
  }, [dealers]);

  const handleFilterChange = (event) => {
    setSelectedTimeFilter(event.target.value);
  };

  const StatusBadge = ({ status }) => {
    const base = 'px-2 py-[2px] rounded-full text-xs font-medium flex items-center gap-1 justify-self-start';
    return status === 'Active'
      ? <span className={`${base} bg-green-100 text-green-900`}><span className="h-2 w-2 bg-green-500 rounded-full" />Active</span>
      : <span className={`${base} bg-yellow-100 text-yellow-900`}><span className="h-2 w-2 bg-yellow-500 rounded-full" />Inactive</span>;
  };

  const DealerSummaryCard = ({ percent, name, label, color }) => {
    const radius = 30;
    const stroke = 6;
    const normalizedRadius = radius - stroke * 0.5;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (percent / 100) * circumference;

    return (
      <div className="flex items-center space-x-3 border rounded-xl px-4 py-3 shadow-sm bg-white">
        <svg height={radius * 2} width={radius * 2}>
          <circle
            stroke="#e5e7eb"
            fill="transparent"
            strokeWidth={stroke}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          <circle
            stroke={color}
            fill="transparent"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference + ' ' + circumference}
            style={{ strokeDashoffset }}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            transform={`rotate(-90 ${radius} ${radius})`}
          />
          <text
            x="50%"
            y="50%"
            dy="0.3em"
            textAnchor="middle"
            className="text-sm font-semibold"
          >
            {percent}%
          </text>
        </svg>
        <div>
          <h4 className="font-semibold text-gray-800">{name}</h4>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    );
  };

  const columns = [
    { label: 'Dealers', render: (d) => <span className="text-blue-600 font-semibold">{d.username}</span> },
    { label: 'Mobile Number', key: 'mobileNo' },
    { label: 'Email', render: (d) => d.email || '-' },
    { label: 'Status', render: (d) => <StatusBadge status={d.isActive ? 'Active' : 'Inactive'} /> },
    {
      label: 'Created At',
      render: (d) => (d.createdAt ? moment(d.createdAt).format('DD MMMM, YYYY, hh:mm A') : '-'),
    },
    { label: 'Assigned FASTag', render: (d) => d.assigned || 0 },
    { label: 'Activated FASTag', render: (d) => d.activated || 0 },
    {
      label: 'Show Agent',
      render: (d) => (
        <button
          className="text-blue-600 font-medium underline cursor-pointer"
          onClick={() => { setSelectedDealerForAgents(d); setAgentsModalOpen(true); }}
        >
          View Agents
        </button>
      ),
    },
    {
      label: 'Edit',
      tdClassName: 'flex justify-end',
      render: (d) => (
        <button
          onClick={() => setEditDealer(d)}
          className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
          title="Edit dealer"
        >
          <Pencil size={16} />
        </button>
      ),
    },
  ];

  const topDealersData = summaryData?.topDealers || [];
  const dealersSummary = topDealersData
    ?.filter(d => d?.dealerDetails)
    ?.map(d => ({
      name: d.dealerDetails?.username || 'Unknown',
      percent: (d.totalAssigned > 0) ? Math.min(100, parseFloat(d.percentage) || 0) : 0,
      label: `Rank ${d.rank}`,
      color: d.rank === 1 ? '#22c55e' : d.rank === 2 ? '#f59e0b' : '#ef4444'
    }));

  return (
    <div className="p-0 space-y-8">
      {/* Dealers Summary */}
      <div className="">
        <div className="bg-[#FFFFFF] p-6 rounded-xl ">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800 text-[20px]">Dealers Summary</h2>
            <select
              className="text-sm text-gray-600 bg-transparent outline-none cursor-pointer"
              value={selectedTimeFilter}
              onChange={handleFilterChange}
            >
              <option value="thisMonth">This Month</option>
              <option value="LastMonth">Last Month</option>
              <option value="thisYear">This Year</option>
            </select>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-4 mb-[21px]">
            {(dealersSummary && dealersSummary.length > 0 ? dealersSummary : [
              { name: "N/A", percent: 0, label: "Rank 1", color: "#22C55E" },
              { name: "N/A", percent: 0, label: "Rank 2", color: "#F59E0B" },
              { name: "N/A", percent: 0, label: "Rank 3", color: "#EF4444" },
            ]).map((dealer, i) => (
              <DealerSummaryCard
                key={i}
                percent={dealer.percent}
                name={dealer.name}
                label={dealer.label}
                color={dealer.color}
              />
            ))}
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex justify-between items-center mt-6 mb-6 md:gap-10 gap-6 relative lg:flex-row flex-col">
          <div className="flex !w-full items-center gap-2 md:flex-row flex-col">
            {/* Search Input with Search Icon */}
            <div className="relative md:w-60 w-full">
              <TextInput
                name="search"
                placeholder="Mobile Number"
                value={search}
                onChange={(e) => { setCurrentPage(1); setSearch(e.target.value); }}
                inputClass="pl-5 pr-3 py-[12.5px] text-sm"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                <Search className="w-4 h-4" />
              </div>
            </div>

            {/* Dealer Dropdown using CustomSelectInput */}
            <div className="relative md:w-60 w-full ">
              <CustomSelectInput
                name="selectedDealer"
                label=""
                value={selectedDealer}
                placeholder="Select Dealer"
                onChange={(e) => { setCurrentPage(1); setSelectedDealer(e.target.value); }}
                options={[
                  { name: "All Dealers", value: "" },
                  ...dealerDataNames.map((d) => ({ name: d.username, value: d.username })),
                ]}
                showSearch={true}
                inputClass="py-[12.5px] px-3 text-sm text-gray-500"
              />
            </div>
          </div>

          {/* Add Button */}
          <div className="relative">
            <Button
              label="Add +"
              onClick={() => setAddOpen(true)}
              roundedButton
              className="!bg-red-500 hover:!bg-red-600 !text-white rounded-full px-5 py-2"
            />
          </div>
        </div>

        {/* Dealers Table */}
        <CommonTable
          columns={columns}
          data={dealers}
          emptyMessage={loading ? 'Loading...' : 'No Dealers Found'}
          setCurrentPage={setCurrentPage}
          setItemsPerPage={setItemsPerPage}
          currentPage={currentPage}
          isPagination={true}
          itemsPerPage={itemsPerPage}
          total={total}
          totalPages={totalPages}
          loading={loading}
          hideDateAndDownload={true}
        />
      </div>

      {/* Add Dealer modal */}
      <AddAgent
        modalOpen={addOpen}
        setModalOpen={setAddOpen}
        api="CreateDealer"
        title="Add Dealer"
        onClose={() => { setAddOpen(false); setRefresh((n) => n + 1); }}
      />

      {/* Dealer's agents modal */}
      {agentsModalOpen && selectedDealerForAgents && (
        <DealerAgentsModal
          dealer={selectedDealerForAgents}
          onClose={() => { setAgentsModalOpen(false); setSelectedDealerForAgents(null); }}
          setModalOpen={setAgentsModalOpen}
        />
      )}

      {/* Edit dealer modal — email + permissions */}
      <EditUserModal
        user={editDealer}
        roleLabel="dealer"
        // showActivation
        onClose={() => setEditDealer(null)}
        onSaved={() => setRefresh((n) => n + 1)}
      />
    </div>
  );
};

export default Dealer;
